/** Eight counted people total. See docs/room-person-cap.md. */
(function(root, factory) {
    const capacity = factory();
    if (typeof module === 'object' && module.exports) module.exports = capacity;
    else root.MalkaCallCapacity = capacity;
})(typeof globalThis !== 'undefined' ? globalThis : this, function() {
    const MAX_CALL_PARTICIPANTS = 8;
    const ROOM_CAP_EXCLUDED_ROLES = Object.freeze([ 'observer', 'service', 'team_interpreter' ]);
    // The composer's address rules (client-profile.js isHandleLike/isEmailLike).
    const HANDLE = /^@[a-z0-9_.-]{2,}$/i;
    const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    function maxInvitees(interpreterRequested) {
        return MAX_CALL_PARTICIPANTS - 1 - (interpreterRequested ? 1 : 0);
    }
    // Input order is selection order. Preserve the oldest selections.
    function fitParticipants(participants, interpreterRequested) {
        const limit = maxInvitees(interpreterRequested);
        return { participants: participants.slice(0, limit), removed: participants.slice(limit) };
    }
    function removalNote(removed, interpreterRequested) {
        if (!removed.length) return '';
        const names = removed.map(person => person.name || 'Last participant').join(', ');
        return interpreterRequested
            ? `${names} removed. Maximum 8 people, including you and the interpreter.`
            : `${names} removed. Maximum 8 people, including you.`;
    }
    /**
     * What a typed entry can be invited as: an @handle or an email, or null.
     * Names and numbers are not invitations: a name is a search, and a number
     * is placed with an interpreter.
     */
    function inviteTarget(text) {
        const value = String(text || '').trim();
        if (HANDLE.test(value)) return { handle: value };
        if (EMAIL.test(value)) return { email: value };
        return null;
    }
    function addressKey(value) {
        const text = String(value || '').trim();
        if (!text) return '';
        if (HANDLE.test(text)) return `handle:${text.slice(1).toLowerCase()}`;
        if (EMAIL.test(text)) return `email:${text.toLowerCase()}`;
        if (/[a-z@]/i.test(text)) return '';
        const digits = text.replace(/\D/g, '');
        if (digits.length < 7) return '';
        // A bare ten digits is NANP, as the dial preview reads it.
        return `phone:${!text.startsWith('+') && digits.length === 10 ? `1${digits}` : digits}`;
    }
    /**
     * Every identity that names this person: account, handle, email, number.
     * A saved contact and a recent call are two entries for one person; they
     * share at least one of these.
     */
    function participantKeys(participant) {
        const person = participant || {};
        const keys = [];
        const clientId = String(person.clientId || person.linkedClientId || '').trim();
        if (clientId) keys.push(`client:${clientId}`);
        [ person.target, person.handle, person.email, person.phone, person.phoneNumber ].forEach(value => {
            const key = addressKey(value);
            if (key && !keys.includes(key)) keys.push(key);
        });
        return keys;
    }
    function sameParticipant(a, b) {
        const keys = participantKeys(a);
        return participantKeys(b).some(key => keys.includes(key));
    }
    // First occurrence wins, so the order people were picked in is kept.
    function uniqueParticipants(participants) {
        return (participants || []).reduce((kept, person) =>
            kept.some(other => other === person || sameParticipant(other, person)) ? kept : [ ...kept, person ], []);
    }
    function roomPersonId(person) {
        const id = person && (person.malkaPersonId || person.personId || person.id);

        return typeof id === 'string' ? id.trim() : '';
    }
    function roomPersonRole(person) {
        const role = person && (person.malkaPersonRole || person.personRole || person.role);

        return typeof role === 'string' ? role.trim().toLowerCase() : '';
    }
    function countsTowardRoomCap(person) {
        const id = roomPersonId(person);
        const role = roomPersonRole(person);

        return Boolean(id && role && !ROOM_CAP_EXCLUDED_ROLES.includes(role));
    }
    function countRoomPeople(people) {
        return new Set((people || []).filter(countsTowardRoomCap).map(roomPersonId)).size;
    }
    /**
     * Node port of deploy/jitsi/prosody/mod_muc_person_cap.lua. The media
     * guard is authoritative; this keeps server seat rules and fixtures on
     * exactly the same identity/role definition.
     */
    function decideRoomPersonAdmission({ entrant, limit = MAX_CALL_PARTICIPANTS, mode = 'enforce', occupants = [] }) {
        if (![ 'off', 'log', 'enforce' ].includes(mode)) throw new Error(`Unknown room person cap mode: ${mode}`);
        if (!Number.isInteger(limit) || limit < 1) throw new Error('Room person cap must be a positive integer');
        if (mode === 'off') return { admitted: true, count: countRoomPeople(occupants), reason: 'off' };

        const id = roomPersonId(entrant);
        const role = roomPersonRole(entrant);
        const count = countRoomPeople(occupants);

        if (!id || !role) return { admitted: mode === 'log', count, reason: 'missing_claims' };
        if (!countsTowardRoomCap(entrant)) return { admitted: true, count, reason: 'excluded' };
        if (occupants.some(person => countsTowardRoomCap(person) && roomPersonId(person) === id)) {
            return { admitted: true, count, reason: 'duplicate' };
        }
        if (count < limit) return { admitted: true, count, reason: 'available' };

        return { admitted: mode === 'log', count, reason: 'full' };
    }
    /**
     * People in a room as its own screens count them against the cap: the
     * local person and everyone else in it, but one interpreter at most. A
     * second interpreter is the call's team interpreter
     * (docs/interpreter-teaming.md) and is not a counted person, as the
     * server's seat rules never count one. `remote` holds the room's other
     * participants with the role each announced (malkaVisualRole).
     */
    function countCallPeople(remote, localIsInterpreter) {
        let people = localIsInterpreter ? 0 : 1;
        let interpreters = localIsInterpreter ? 1 : 0;
        for (const participant of remote || []) {
            if (participant && participant.malkaVisualRole === 'interpreter') interpreters += 1;
            else people += 1;
        }
        return people + Math.min(1, interpreters);
    }
    return {
        MAX_CALL_PARTICIPANTS,
        ROOM_CAP_EXCLUDED_ROLES,
        countCallPeople,
        countRoomPeople,
        countsTowardRoomCap,
        decideRoomPersonAdmission,
        maxInvitees,
        fitParticipants,
        removalNote,
        inviteTarget,
        participantKeys,
        sameParticipant,
        uniqueParticipants
    };
});
